import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import PageHeading from '../../components/PageHeading';
import Loading from '../../components/Loading';
import StepEdit, { MARKETING_AUTOMATION_APPLIES_TO_OPTIONS } from '../../components/flows/StepEdit';
import SectionPicker from '../../components/SectionPicker';
import MarketingAutomationLogs from '../../components/MarketingAutomationLogs';
import ConditionMatchesPreview from '../../components/ConditionMatchesPreview';
import {
    useMarketingAutomationDetailsQuery,
    useMarketingSchemaQuery,
    setMarketingAutomationDetailsQuery,
    setMarketingAutomationsQuery
} from '../../lib/Queries';
import useBusinessPutApi from '../../lib/useBusinessPutApi';
import useBusinessDeleteApi from '../../lib/useBusinessDeleteApi';
import useBusinessNavigate from '../../lib/useBusinessNavigate';
import { ObjectTypes } from '~/lib/ObjectTypes.js';
import { ucfirst } from '~/lib/Utils.js';
import * as Toast from '~/lib/Toast.js';
import InfoMessageBlock from '~/components/InfoMessageBlock.jsx';
import ErrorMessageBlock from '~/components/ErrorMessageBlock.jsx';

const MarketingEditAutomationPage = () => {
    const { id } = useParams();
    const location = useLocation();
    const automationId = parseInt(id);
    const isNew = automationId === 0;
    const navigate = useBusinessNavigate();

    // Get template data from navigation state
    const templateData = location.state?.templateData;

    const [section, setSection] = useState('edit');
    const [data, setData] = useState(templateData || {
        trigger: 'condition_met',
        action: 'email',
        offset: 0,
        condition: { andor: 'and', conditions: [] },
        target_conditions: { andor: 'and', conditions: [] },
        settings: {},
        flowable_type: 'App\\Models\\Client', // Default to clients
    });

    const { data: automationData, isLoading } = useMarketingAutomationDetailsQuery(automationId);
    const { data: schemaData } = useMarketingSchemaQuery(data.flowable_type);
    const schema = schemaData?.schema || [];

    const { update: updateAutomation, isUpdating } = useBusinessPutApi('/marketing-automations');
    const { deleteRequest: deleteAutomation, isDeleting } = useBusinessDeleteApi('/marketing-automations');

    // Load existing automation data
    useEffect(() => {
        if (!isNew && automationData?.automation) {
            setData(automationData.automation);
        }
    }, [isNew, automationData]);
    console.log('data = ', data)

    const handleSave = async (stepData) => {
        try {
            // Use automationId (which is 0 for new automations) for both create and update
            const result = await updateAutomation(automationId, stepData);
            if (result?.automation) {
                // Update the cache with the automation
                setMarketingAutomationDetailsQuery(result.automation.id, { automation: result.automation });
                // Update the list cache
                if (result.automations) {
                    setMarketingAutomationsQuery({ automations: result.automations });
                }

                Toast.success('Successfully saved');
                navigate(`/marketing`);
                // if (isNew) {
                //     // Navigate to the edit page for the new automation
                //
                // } else {
                //     // Update local state for existing automation
                //     setData(result.automation);
                // }
            }
        } catch (error) {
            console.error('Error saving marketing automation:', error);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this automation?')) {
            return;
        }

        try {
            const result = await deleteAutomation(automationId);
            if (result) {
                // Update the list cache
                if (result.automations) {
                    setMarketingAutomationsQuery({ automations: result.automations });
                }
                // Navigate back to the index page
                navigate('/marketing');
            }
        } catch (error) {
            console.error('Error deleting marketing automation:', error);
        }
    };

    const handleCancel = () => {
        navigate('/marketing');
    };

    if (isLoading && !isNew) {
        return <Loading className="py-4" />;
    }

    const applyToName = ucfirst(MARKETING_AUTOMATION_APPLIES_TO_OPTIONS[data.flowable_type]);

    return (
        <div>
            <PageHeading
                crumbs={[{ name: 'Marketing Automations', href: '/marketing' }]}
                heading={
                    isNew
                        ? `New ${applyToName} Automation`
                        : `Edit ${applyToName} Automation`
                }
            />

            {!isNew && data.is_enabled === false && (
                <ErrorMessageBlock className="mb-4 max-w-4xl">
                    This automation is currently disabled and will not run. Enable it from the Automations page to start sending messages.
                </ErrorMessageBlock>
            )}

            {!isNew && (
                <SectionPicker
                    className="mb-4"
                    value={section}
                    onChange={setSection}
                    options={[
                        { value: 'edit', label: 'Edit' },
                        { value: 'logs', label: 'Logs' },
                    ]}
                />
            )}

            {section === 'edit' && (
                <div className="max-w-4xl relative">
                    {isLoading && <Loading />}
                    {!isLoading && (
                        <StepEdit
                            key={data.id}
                            data={data}
                            setData={setData}
                            onSave={handleSave}
                            onCancel={handleCancel}
                            onDelete={!isNew ? handleDelete : undefined}
                            isSaving={isUpdating}
                            isDeleting={isDeleting}
                            middleContent={
                                <ConditionMatchesPreview
                                    className="mt-4"
                                    automationId={automationId}
                                    schema={schema}
                                    conditions={data.target_conditions}
                                    flowableType={data.flowable_type}
                                    action={data.action}
                                />
                            }
                            triggerTypes={['condition_met']} // Only condition-based triggers
                            isMarketingAutomation={true}
                            schema={schema}
                        />
                    )}
                </div>
            )}

            {section === 'logs' && (
                <div className="max-w-4xl">
                    <MarketingAutomationLogs automationId={automationId} />
                </div>
            )}
        </div>
    );
};

export default MarketingEditAutomationPage;
